import { now as clockNow } from '../Clock.ts';
import type { GameState } from '../game/GameState.ts';
import { EventType } from '../experiments/Events.ts';
import type { EventLogger } from '../experiments/EventLogger.ts';
import { SafetyGate, type SafetyLimits } from './SafetyGate.ts';
import { MockStimulator } from './MockStimulator.ts';
import type { Stimulator, StimulatorStatus, StimulusRequest, StimulusResult } from './Stimulator.ts';

export interface StimulusIntent {
  requestId: string;
  tick: number;
  simTimeMs: number;
  gameState?: GameState;
  channel?: number;
  durationMs?: number;
  reason?: string;
  trialId?: string;
}

export interface StimulationCounters {
  requested: number;
  accepted: number;
  rejected: number;
  delivered: number;
}

/**
 * The one door between intent and stimulation hardware.
 *
 * Nothing in this codebase calls `Stimulator.requestStimulus` directly. Every
 * request goes through here, which means every request is rate limited, is
 * checked against the armed state, and is logged -- including the ones that get
 * refused. A refused request that vanished silently would look identical to a
 * participant who failed to respond, and those are very different findings.
 *
 * The service also owns the emergency stop, because stopping stimulation and
 * stopping autonomous control have to happen together: a bot that keeps
 * deciding to flap while the stimulator is dead is just filling the log with
 * rejections.
 */
export class StimulationService {
  readonly gate: SafetyGate;

  private current: Stimulator;
  private counters: StimulationCounters = {
    requested: 0, accepted: 0, rejected: 0, delivered: 0,
  };
  private pendingEnds: { requestId: string; at: number; tick: number; trialId?: string }[] = [];
  private stopListeners = new Set<(reason: string) => void>();

  /** Default pulse duration when an intent does not specify one, ms. */
  defaultDurationMs = 60;
  /** Default hardware channel. */
  defaultChannel = 0;

  constructor(
    private readonly logger: EventLogger,
    limits: Partial<SafetyLimits> = {},
    stimulator: Stimulator = new MockStimulator(),
  ) {
    this.gate = new SafetyGate(limits);
    this.current = stimulator;
  }

  get stimulator(): Stimulator {
    return this.current;
  }

  /**
   * Swap the actuator. Always disarms first: an armed state established for one
   * device must never carry over to another, least of all to a real one.
   */
  async setStimulator(next: Stimulator, tick = 0): Promise<void> {
    await this.disarm(tick);
    try {
      await this.current.disconnect();
    } catch {
      // A stimulator that cannot be cleanly disconnected is still being replaced.
    }
    this.current = next;
    this.logger.log(EventType.CONFIG_CHANGED, {
      tick,
      metadata: { stimulator: next.kind, label: next.label, simulated: next.simulated },
    });
  }

  async connect(tick = 0): Promise<void> {
    await this.current.connect();
    this.logger.log(EventType.STIMULATOR_CONNECTED, {
      tick,
      metadata: { kind: this.current.kind, simulated: this.current.simulated },
    });
  }

  async disconnect(tick = 0): Promise<void> {
    await this.disarm(tick);
    await this.current.disconnect();
    this.logger.log(EventType.STIMULATOR_DISCONNECTED, { tick, metadata: { kind: this.current.kind } });
  }

  // ------------------------------------------------------------------ arming

  /**
   * Arm. Requires a connected stimulator and no latched emergency stop.
   * This is the only path by which stimulation becomes possible, and it is
   * reachable only from an explicit operator control in the UI.
   */
  async arm(tick = 0): Promise<void> {
    await this.current.arm();
    this.gate.arm();
    this.logger.log(EventType.STIM_ARMED, {
      tick,
      metadata: {
        kind: this.current.kind,
        simulated: this.current.simulated,
        limits: this.gate.limits,
        operatorIntensityNote: this.current.getStatus().operatorIntensityNote,
      },
    });
  }

  async disarm(tick = 0): Promise<void> {
    if (!this.gate.isArmed && !this.current.getStatus().armed) return;
    await this.current.disarm();
    this.gate.disarm();
    this.logger.log(EventType.STIM_DISARMED, { tick, metadata: { kind: this.current.kind } });
  }

  /**
   * Emergency stop. Synchronous, idempotent, and safe to call from a click
   * handler, a keypress, or an error path.
   */
  emergencyStop(reason = 'operator', tick = 0): void {
    this.current.emergencyStop();
    this.gate.emergencyStop();
    this.pendingEnds = [];
    this.logger.log(EventType.EMERGENCY_STOP, { tick, metadata: { reason, kind: this.current.kind } });
    for (const fn of this.stopListeners) fn(reason);
  }

  /** Clear the latch. Deliberately a separate, explicit operator action. */
  clearEmergencyStop(tick = 0): void {
    if ('clearEmergencyStop' in this.current) {
      (this.current as { clearEmergencyStop(): void }).clearEmergencyStop();
    }
    this.gate.clearEmergencyStop();
    this.logger.log(EventType.CONFIG_CHANGED, { tick, metadata: { emergencyStopCleared: true } });
  }

  /** Notified on every emergency stop, so autonomous control can be paused. */
  onEmergencyStop(fn: (reason: string) => void): () => void {
    this.stopListeners.add(fn);
    return () => this.stopListeners.delete(fn);
  }

  // ----------------------------------------------------------------- request

  /**
   * Ask for one stimulation.
   *
   * Emits STIM_REQUESTED, then either STIM_REJECTED (safety layer or device
   * refusal) or STIM_STARTED, with STIM_ENDED following at the reported end
   * time. Returns the device's own result; the caller must not treat
   * `accepted: true` as evidence that the participant moved.
   */
  async requestStimulus(intent: StimulusIntent): Promise<StimulusResult> {
    const now = clockNow();
    const request: StimulusRequest = {
      requestId: intent.requestId,
      requestedAt: now,
      channel: intent.channel ?? this.defaultChannel,
      durationMs: intent.durationMs ?? this.defaultDurationMs,
      reason: intent.reason,
    };

    this.counters.requested++;
    this.logger.log(EventType.STIM_REQUESTED, {
      tick: intent.tick,
      timestamp: now,
      simTimeMs: intent.simTimeMs,
      gameState: intent.gameState,
      requestId: request.requestId,
      trialId: intent.trialId,
      metadata: {
        channel: request.channel,
        durationMs: request.durationMs,
        reason: request.reason,
        stimulator: this.current.kind,
      },
    });

    const decision = this.gate.check(request, now);
    if (!decision.allowed) {
      this.counters.rejected++;
      this.logger.log(EventType.STIM_REJECTED, {
        tick: intent.tick,
        simTimeMs: intent.simTimeMs,
        requestId: request.requestId,
        trialId: intent.trialId,
        metadata: { reason: decision.reason, source: 'SafetyGate' },
      });
      return { requestId: request.requestId, accepted: false, rejectedReason: decision.reason };
    }
    request.durationMs = decision.durationMs;

    let result: StimulusResult;
    try {
      result = await this.current.requestStimulus(request);
    } catch (err) {
      this.counters.rejected++;
      this.logger.log(EventType.STIM_FAILED, {
        tick: intent.tick,
        simTimeMs: intent.simTimeMs,
        requestId: request.requestId,
        trialId: intent.trialId,
        metadata: { error: String(err) },
      });
      return { requestId: request.requestId, accepted: false, error: String(err) };
    }

    if (!result.accepted) {
      this.counters.rejected++;
      this.logger.log(EventType.STIM_REJECTED, {
        tick: intent.tick,
        simTimeMs: intent.simTimeMs,
        requestId: request.requestId,
        trialId: intent.trialId,
        metadata: { reason: result.rejectedReason ?? 'device refused', source: 'device' },
      });
      return result;
    }

    this.counters.accepted++;
    this.counters.delivered++;
    this.gate.commit(result.startedAt ?? now);

    this.logger.log(EventType.STIM_STARTED, {
      tick: intent.tick,
      timestamp: result.startedAt ?? now,
      simTimeMs: intent.simTimeMs,
      requestId: request.requestId,
      trialId: intent.trialId,
      metadata: { durationMs: request.durationMs, channel: request.channel },
    });

    if (result.endedAt !== undefined) {
      this.pendingEnds.push({
        requestId: request.requestId,
        at: result.endedAt,
        tick: intent.tick,
        trialId: intent.trialId,
      });
    }

    return result;
  }

  /**
   * Advance time-based state: emit STIM_ENDED events as their pulses finish,
   * and give the stimulator its own clock tick.
   */
  pump(nowMs: number, tick: number): void {
    if (this.pendingEnds.length > 0) {
      const due = this.pendingEnds.filter((p) => p.at <= nowMs);
      if (due.length > 0) {
        this.pendingEnds = this.pendingEnds.filter((p) => p.at > nowMs);
        for (const p of due) {
          this.logger.log(EventType.STIM_ENDED, {
            tick,
            timestamp: p.at,
            requestId: p.requestId,
            trialId: p.trialId,
          });
        }
      }
    }
    this.current.pump?.(nowMs);
  }

  getStatus(): StimulatorStatus {
    const status = this.current.getStatus();
    // The gate is the authority on armed state; a device that disagrees is a bug
    // and should read as disarmed rather than as armed.
    return { ...status, armed: status.armed && this.gate.isArmed };
  }

  getCounters(): StimulationCounters {
    return { ...this.counters };
  }

  resetCounters(): void {
    this.counters = { requested: 0, accepted: 0, rejected: 0, delivered: 0 };
    this.gate.resetSession();
  }
}
